/**
 * Brake phase from the per-GPS-point signal.
 *
 * WHY this exists at all: the brake-on / brake-off map markers must sit on the
 * trace the rider is looking at. Session Analysis plots Brake % from the
 * per-point `processedTelemetryData.acceleration[i].longitudinalMs2 / 9.81`
 * (index-aligned with `gpsPoints[i]`), but the marker positions used to come
 * from `ZoneDataPoint.acceleration` — a different, coarser signal: GPS
 * bucket-speed dv/dt on a 5 m grid, where a bucket is LABELLED with its floor
 * key while holding the GPS point nearest key + 2.5 m, the interval's accel is
 * assigned to the later bucket's label, and the result is 3-pass Gaussian
 * smoothed and then 9-point median filtered at the lap level. Every one of
 * those steps biases the reading early. Measured on real session data, the
 * bucket-derived brake-off marker landed 2-11 m (median 6 m) BEFORE the point
 * where the chart's own trace drops below 0.3 g, on every lap, with the chart
 * still reading 0.32-0.55 g at the marker.
 *
 * Two things are fixed here. The signal: brake-on, brake-off and peak braking
 * g all come from the same per-point series the chart draws. And the
 * definition: release is the 0.3 g CROSSING (linearly interpolated between the
 * bracketing samples), not the last sample still beyond 0.3 g, which is what
 * the bucket path reported.
 *
 * This module is deliberately pure and free of domain types so the zone code
 * can feed it a slice and fall back to the bucket path (unchanged) whenever a
 * lap carries no usable per-point acceleration.
 */

import { LAP_TABLE_BRAKING_G, BRAKE_RELEASE_G } from '../config/zoneDetection'

export interface BrakePhaseSample {
  /**
   * Lap-relative distance (metres) — `gpsPoints[i].distanceMeters`, the same
   * frame as `ZoneDataPoint.distance` and `SpatialBucket.distance`.
   */
  distance: number
  /** km/h */
  speed: number
  /**
   * Signed longitudinal g (+ accelerating, − braking). Undefined when the
   * point has no acceleration sample or the sample is flagged
   * `speedTrendMismatch` — exactly the points where the chart draws a gap.
   */
  accelerationG?: number
}

export interface BrakePhaseResult {
  /** Zone-relative metres, or null when the slice never reaches braking. */
  brakingPointDistance: number | null
  /** Zone-relative metres, or null when braking never passes the release gate. */
  brakeReleaseDistance: number | null
  /** Positive magnitude of the deepest deceleration in the slice; 0 if none. */
  peakBrakingG: number
}

/**
 * Derive the brake phase from the chart's own per-point signal.
 *
 * Returns null when fewer than two samples carry a defined g — the caller then
 * falls back to the bucket-derived metrics rather than guessing from a single
 * reading.
 */
export function findBrakePhaseFromPoints(
  samples: BrakePhaseSample[],
  zoneStartDistance: number
): BrakePhaseResult | null {
  const definedCount = samples.reduce(
    (count, s) => (s.accelerationG !== undefined ? count + 1 : count),
    0
  )
  if (definedCount < 2) return null

  const apexIdx = findApexIndex(samples)

  return {
    brakingPointDistance: findBrakingPoint(samples, zoneStartDistance),
    brakeReleaseDistance: findRelease(samples, apexIdx, zoneStartDistance),
    peakBrakingG: findPeakBrakingG(samples),
  }
}

/** First occurrence of the minimum speed, over every sample (defined g or not). */
function findApexIndex(samples: BrakePhaseSample[]): number {
  let apexIdx = 0
  let minSpeed = Infinity
  for (let i = 0; i < samples.length; i++) {
    if (samples[i].speed < minSpeed) {
      minSpeed = samples[i].speed
      apexIdx = i
    }
  }
  return apexIdx
}

/**
 * Where the trace first drops through LAP_TABLE_BRAKING_G. Interpolated from
 * the previous defined sample when there is one, so the marker sits on the
 * crossing rather than on the first sample already past it.
 */
function findBrakingPoint(
  samples: BrakePhaseSample[],
  zoneStartDistance: number
): number | null {
  let previousDefinedIdx = -1

  for (let i = 0; i < samples.length; i++) {
    const g = samples[i].accelerationG
    if (g === undefined) continue

    if (g < LAP_TABLE_BRAKING_G) {
      if (
        previousDefinedIdx >= 0 &&
        samples[previousDefinedIdx].accelerationG! >= LAP_TABLE_BRAKING_G
      ) {
        return (
          interpolateCrossing(
            samples[previousDefinedIdx],
            samples[i],
            LAP_TABLE_BRAKING_G
          ) - zoneStartDistance
        )
      }
      return samples[i].distance - zoneStartDistance
    }

    previousDefinedIdx = i
  }

  return null
}

/**
 * Where braking is released: the LAST heavy-braking sample at or before the
 * apex, carried forward to the BRAKE_RELEASE_G crossing with the next defined
 * sample. A dip back below the gate after the apex is trail braking / corner
 * exit, never the release.
 */
function findRelease(
  samples: BrakePhaseSample[],
  apexIdx: number,
  zoneStartDistance: number
): number | null {
  let lastHeavyIdx = -1
  for (let i = 0; i <= apexIdx && i < samples.length; i++) {
    const g = samples[i].accelerationG
    if (g !== undefined && g < BRAKE_RELEASE_G) {
      lastHeavyIdx = i
    }
  }
  if (lastHeavyIdx < 0) return null

  const nextDefinedIdx = findNextDefinedIndex(samples, lastHeavyIdx)
  if (nextDefinedIdx < 0) {
    return samples[lastHeavyIdx].distance - zoneStartDistance
  }

  const nextG = samples[nextDefinedIdx].accelerationG!
  if (nextG < BRAKE_RELEASE_G) {
    // Only reachable past the apex — braking never eased, so the last heavy
    // sample before the apex is the best statement of the release.
    return samples[lastHeavyIdx].distance - zoneStartDistance
  }

  return (
    interpolateCrossing(
      samples[lastHeavyIdx],
      samples[nextDefinedIdx],
      BRAKE_RELEASE_G
    ) - zoneStartDistance
  )
}

function findNextDefinedIndex(
  samples: BrakePhaseSample[],
  fromIdx: number
): number {
  for (let i = fromIdx + 1; i < samples.length; i++) {
    if (samples[i].accelerationG !== undefined) return i
  }
  return -1
}

/** Deepest deceleration anywhere in the slice, as a positive magnitude. */
function findPeakBrakingG(samples: BrakePhaseSample[]): number {
  let peak = 0
  for (const s of samples) {
    const g = s.accelerationG
    if (g !== undefined && g < 0 && -g > peak) {
      peak = -g
    }
  }
  return peak
}

/**
 * Distance at which the straight line between two samples' g values passes
 * `threshold`. Degenerate cases (equal g, so no crossing to place) fall back
 * to the later sample's own distance.
 */
function interpolateCrossing(
  from: BrakePhaseSample,
  to: BrakePhaseSample,
  threshold: number
): number {
  const fromG = from.accelerationG!
  const toG = to.accelerationG!
  const span = toG - fromG
  if (span === 0) return to.distance

  const fraction = (threshold - fromG) / span
  return from.distance + fraction * (to.distance - from.distance)
}
